import { useState, useEffect, useMemo, useCallback } from "react";
import { format, addDays, subDays, startOfWeek, addWeeks, subWeeks } from "date-fns";
import { shiftApi, businessHoursApi, hoursOverrideApi, eventsApi, employeeApi } from "../lib/api";
import { getCurrencySymbol, calculateBillableHours, getEffectiveHoursForDate, isPastDate } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CalendarIcon, ChevronLeft, ChevronRight, Clock, LayoutGrid, AlignJustify, User, Users, FileText, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { useAuth } from "../lib/AuthContext";
import { ScrollArea, ScrollBar } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";

// Import from extracted components
import { 
  ROW_HEIGHT, 
  STORAGE_KEY, 
  getEventColor, 
  colorWithOpacity 
} from "../components/staff-dashboard/constants";
import EventItem from "../components/staff-dashboard/EventItem";

// Desktop layout options (Timeline, Cards, My Shifts)
const DESKTOP_LAYOUT_OPTIONS = [
  { id: "timeline", label: "Timeline", icon: AlignJustify, description: "Traditional timeline bars" },
  { id: "cards", label: "Cards", icon: LayoutGrid, description: "Stacked shift cards" },
  { id: "myshifts", label: "My Shifts", icon: User, description: "Focus on your schedule" },
];

// Mobile layout options (just All Staff vs My Shifts toggle)
const MOBILE_LAYOUT_OPTIONS = [
  { id: "cards", label: "All Staff", icon: Users },
  { id: "myshifts", label: "My Shifts", icon: User },
];

const StaffDashboard = () => {
  const { user, company, hasFeatureAccess } = useAuth();
  
  // Load preferences from localStorage
  const getSavedPreferences = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error("Failed to parse preferences:", e);
    }
    return { defaultView: "day", defaultLayout: "cards", showNotWorking: false };
  };

  const savedPrefs = getSavedPreferences();
  
  const [view, setView] = useState(savedPrefs.defaultView || "day"); // 'day' | 'week'
  const [layout, setLayout] = useState(savedPrefs.defaultLayout || "cards"); // 'timeline' | 'cards' | 'myshifts'
  const [showNotWorking, setShowNotWorking] = useState(savedPrefs.showNotWorking ?? false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [events, setEvents] = useState([]);
  const [businessHours, setBusinessHours] = useState(null);
  const [hoursOverrides, setHoursOverrides] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check for event management access
  const hasEventAccess = hasFeatureAccess('eventManagement');

  // Calculate week start (Monday)
  const weekStart = useMemo(() => startOfWeek(selectedDate, { weekStartsOn: 1 }), [selectedDate]);
  const weekStartStr = format(weekStart, 'yyyy-MM-dd');
  const weekEndStr = format(addDays(weekStart, 6), 'yyyy-MM-dd');
  
  // Get current employee ID from user
  const currentEmployeeId = user?.employee_id;

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [empRes, hoursRes, overridesRes, shiftsRes] = await Promise.all([
          employeeApi.getAll(),
          businessHoursApi.get(),
          hoursOverrideApi.getAll(),
          shiftApi.getAll(weekStartStr, weekEndStr),
        ]);

        setEmployees(empRes.data);
        setBusinessHours(hoursRes.data);
        setHoursOverrides(overridesRes.data);
        
        // Filter to only show PUBLISHED shifts
        const publishedShifts = shiftsRes.data.filter(s => s.is_published === true);
        setShifts(publishedShifts);

        // Fetch events if user has access
        if (hasEventAccess) {
          try {
            const eventsRes = await eventsApi.getAll(weekStartStr, weekEndStr);
            setEvents(eventsRes.data);
          } catch (error) {
            console.error("Error fetching events:", error);
          }
        }

      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load schedule data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [weekStartStr, weekEndStr, hasEventAccess]);

  // Get current employee's hours for today/this week
  const myHours = useMemo(() => {
    if (!currentEmployeeId) return { day: 0, week: 0 };
    
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    
    // Helper function to calculate hours for a shift
    const getShiftHours = (shift) => {
      if (!shift.start_time || !shift.end_time) return 0;
      return calculateBillableHours(shift, []);
    };
    
    // Calculate day hours
    const dayShifts = shifts.filter(s => s.employee_id === currentEmployeeId && s.date === dateStr);
    const dayHours = dayShifts.reduce((acc, shift) => {
      return acc + getShiftHours(shift);
    }, 0);
    
    // Calculate week hours
    const weekShifts = shifts.filter(s => {
      return s.employee_id === currentEmployeeId && s.date >= weekStartStr && s.date <= weekEndStr;
    });
    const weekHours = weekShifts.reduce((acc, shift) => {
      return acc + getShiftHours(shift);
    }, 0);
    
    return { day: dayHours, week: weekHours };
  }, [shifts, currentEmployeeId, selectedDate, weekStartStr, weekEndStr]);

  // Date navigation
  const goToday = () => setSelectedDate(new Date());
  const goPrev = () => {
    if (view === "day") {
      setSelectedDate(prev => subDays(prev, 1));
    } else {
      setSelectedDate(prev => subWeeks(prev, 1));
    }
  };
  const goNext = () => {
    if (view === "day") {
      setSelectedDate(prev => addDays(prev, 1));
    } else {
      setSelectedDate(prev => addWeeks(prev, 1));
    }
  };

  // Get business hours for a specific day
  const getDayHours = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return getEffectiveHoursForDate(dateStr, businessHours, hoursOverrides);
  }, [businessHours, hoursOverrides]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const dayHours = getDayHours(selectedDate);
  const isToday = format(new Date(), 'yyyy-MM-dd') === dateStr;

  return (
    <div className="space-y-4">
      {/* Header - Simple title */}
      <div className="text-center sm:text-left">
        <h1 className="text-xl font-bold font-['Inter']" data-testid="staff-dashboard-title">My Schedule</h1>
      </div>

      {/* Controls Row - Full width on mobile */}
      <div className="flex flex-col gap-3">
        {/* Mobile: Full width controls */}
        <div className="md:hidden flex items-stretch justify-between gap-2 w-full">
          {/* Day/Week Toggle */}
          <div className="inline-flex rounded-lg border bg-muted/50 p-1">
            <button
              onClick={() => setView("day")}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center",
                view === "day"
                  ? "bg-violet-600 text-white shadow-sm" 
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="staff-day-view-tab-mobile"
            >
              Day
            </button>
            <button
              onClick={() => setView("week")}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center",
                view === "week"
                  ? "bg-violet-600 text-white shadow-sm" 
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="staff-week-view-tab-mobile"
            >
              Week
            </button>
          </div>

          {/* All Staff / My Shifts Toggle */}
          <div className="inline-flex rounded-lg border bg-muted/50 p-1">
            {MOBILE_LAYOUT_OPTIONS.map((option) => {
              const isActive = (option.id === "cards" && layout !== "myshifts") || 
                              (option.id === "myshifts" && layout === "myshifts");
              return (
                <button
                  key={option.id}
                  onClick={() => setLayout(option.id)}
                  className={cn(
                    "flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-all",
                    isActive 
                      ? "bg-violet-600 text-white shadow-sm" 
                      : "text-muted-foreground hover:text-foreground"
                  )}
                  data-testid={`mobile-layout-${option.id}-btn`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Date Navigation - Full width on mobile */}
        <div className="flex items-center gap-2 w-full md:w-auto md:justify-start">
          <Button variant="outline" size="icon" className="h-9 w-9 flex-shrink-0" onClick={goPrev} data-testid="staff-prev-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="flex-1 md:flex-none md:min-w-[200px] h-9 text-sm" data-testid="staff-date-picker">
                <CalendarIcon className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">
                  {view === "day" 
                    ? format(selectedDate, 'EEE, d MMM yyyy')
                    : `${format(weekStart, 'd MMM')} - ${format(addDays(weekStart, 6), 'd MMM yyyy')}`
                  }
                </span>
                <span className="sm:hidden">
                  {view === "day" 
                    ? format(selectedDate, 'EEEE, d MMM')
                    : `${format(weekStart, 'd MMM')} - ${format(addDays(weekStart, 6), 'd MMM')}`
                  }
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="center" sideOffset={8}>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                initialFocus
                className="rounded-md border shadow-lg p-3"
                classNames={{
                  months: "flex flex-col space-y-4",
                  month: "space-y-4",
                  caption: "flex justify-center pt-1 relative items-center",
                  caption_label: "text-base font-medium",
                  nav_button: "h-9 w-9 bg-transparent p-0 opacity-50 hover:opacity-100 border rounded-md",
                  nav_button_previous: "absolute left-1",
                  nav_button_next: "absolute right-1",
                  table: "w-full border-collapse",
                  head_row: "flex",
                  head_cell: "text-muted-foreground rounded-md w-10 font-normal text-sm",
                  row: "flex w-full mt-2",
                  cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected])]:rounded-md",
                  day: "h-10 w-10 p-0 font-normal aria-selected:opacity-100 hover:bg-muted rounded-md",
                  day_selected: "bg-violet-600 text-white hover:bg-violet-600 hover:text-white focus:bg-violet-600 focus:text-white",
                  day_today: "bg-accent text-accent-foreground font-semibold",
                  day_outside: "text-muted-foreground opacity-50",
                  day_disabled: "text-muted-foreground opacity-50",
                }}
              />
            </PopoverContent>
          </Popover>
          
          <Button variant="outline" size="icon" className="h-9 w-9 flex-shrink-0" onClick={goNext} data-testid="staff-next-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Desktop: View Toggle */}
        <Tabs value={view} onValueChange={setView} className="hidden md:block">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="day" data-testid="staff-day-view-tab">Day</TabsTrigger>
            <TabsTrigger value="week" data-testid="staff-week-view-tab">Week</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Desktop/Tablet Layout Options */}
      <div className="hidden md:block space-y-2">
        <p className="text-xs text-muted-foreground font-medium">Display Layout:</p>
        <div className="flex flex-wrap gap-2">
          {DESKTOP_LAYOUT_OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <Button
                key={option.id}
                variant={layout === option.id ? "default" : "outline"}
                size="sm"
                onClick={() => setLayout(option.id)}
                className={cn(
                  "gap-2 transition-all",
                  layout === option.id && "bg-violet-600 hover:bg-violet-700"
                )}
                data-testid={`layout-${option.id}-btn`}
              >
                <Icon className="w-4 h-4" />
                {option.label}
              </Button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          {DESKTOP_LAYOUT_OPTIONS.find(o => o.id === layout)?.description}
        </p>
      </div>

      {/* Hours Summary - Always visible */}
      {myHours && (
        <div className="flex items-center justify-center gap-4 py-2 px-3 bg-violet-50 dark:bg-violet-950/30 rounded-lg text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{view === "day" ? "Today:" : "This Day:"}</span>
            <span className="font-bold text-violet-700 dark:text-violet-300">{myHours.day.toFixed(1)}h</span>
          </div>
          <div className="w-px h-4 bg-violet-200 dark:bg-violet-700" />
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Week:</span>
            <span className="font-bold text-teal-700 dark:text-teal-300">{myHours.week.toFixed(1)}h</span>
          </div>
        </div>
      )}

      {/* Schedule View */}
      {view === "day" ? (
        <StaffDayView
          date={selectedDate}
          employees={employees}
          shifts={shifts}
          events={events}
          businessHours={businessHours}
          hoursOverrides={hoursOverrides}
          currentEmployeeId={currentEmployeeId}
          hasEventAccess={hasEventAccess}
          layout={layout}
          showNotWorking={showNotWorking}
        />
      ) : (
        <StaffWeekView
          weekStart={weekStart}
          employees={employees}
          shifts={shifts}
          events={events}
          businessHours={businessHours}
          hoursOverrides={hoursOverrides}
          currentEmployeeId={currentEmployeeId}
          hasEventAccess={hasEventAccess}
          layout={layout}
          showNotWorking={showNotWorking}
        />
      )}
    </div>
  );
};

// Staff Day View Component
const StaffDayView = ({ date, employees, shifts, events, businessHours, hoursOverrides, currentEmployeeId, hasEventAccess, layout, showNotWorking }) => {
  const dateStr = format(date, 'yyyy-MM-dd');
  const dayHours = getEffectiveHoursForDate(dateStr, businessHours, hoursOverrides);
  
  if (!dayHours?.is_open) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">Business is closed on this day</p>
      </Card>
    );
  }

  const dayShifts = shifts.filter(s => s.date === dateStr);
  const dayEvents = events.filter(e => e.date === dateStr);
  
  // Get employee lookup
  const employeeMap = employees.reduce((acc, emp) => {
    acc[emp.id] = emp;
    return acc;
  }, {});

  // Render based on layout (no more agenda - removed)
  switch (layout) {
    case "cards":
      return (
        <DayCardsLayout
          employees={employees}
          shifts={dayShifts}
          events={dayEvents}
          currentEmployeeId={currentEmployeeId}
          hasEventAccess={hasEventAccess}
          showNotWorking={showNotWorking}
        />
      );
    case "myshifts":
      return (
        <DayMyShiftsLayout
          employees={employees}
          shifts={dayShifts}
          events={dayEvents}
          currentEmployeeId={currentEmployeeId}
          hasEventAccess={hasEventAccess}
          employeeMap={employeeMap}
        />
      );
    default:
      return (
        <DayTimelineLayout
          date={date}
          employees={employees}
          shifts={dayShifts}
          events={dayEvents}
          businessHours={businessHours}
          hoursOverrides={hoursOverrides}
          currentEmployeeId={currentEmployeeId}
          hasEventAccess={hasEventAccess}
        />
      );
  }
};

// ===== DAY LAYOUT COMPONENTS =====

// Layout 1: Timeline (Original Desktop View)
const DayTimelineLayout = ({ date, employees, shifts, events, businessHours, hoursOverrides, currentEmployeeId, hasEventAccess }) => {
  const dateStr = format(date, 'yyyy-MM-dd');
  const dayHours = getEffectiveHoursForDate(dateStr, businessHours, hoursOverrides);
  
  const openMinutes = timeToMinutes(dayHours.open_time);
  const closeMinutes = timeToMinutes(dayHours.close_time);
  const totalMinutes = closeMinutes - openMinutes;

  // Calculate time marks
  const hourMarks = [];
  for (let m = openMinutes; m <= closeMinutes; m += 60) {
    hourMarks.push({
      time: minutesToTime(m),
      percent: ((m - openMinutes) / totalMinutes) * 100
    });
  }

  return (
    <Card className="overflow-hidden">
      {/* Time Header - Hidden on mobile */}
      <div className="relative h-8 bg-muted/30 border-b hidden sm:block" style={{ marginLeft: '150px' }}>
        {hourMarks.map((mark, idx) => (
          <div
            key={idx}
            className="absolute text-xs text-muted-foreground"
            style={{ left: `${mark.percent}%`, transform: 'translateX(-50%)' }}
          >
            {mark.time}
          </div>
        ))}
      </div>

      {/* Events Row */}
      {hasEventAccess && events.length > 0 && (
        <div className="flex border-b bg-slate-50/50 dark:bg-slate-900/30">
          <div className="w-[100px] sm:w-[150px] flex-shrink-0 px-2 sm:px-3 py-2 border-r bg-muted/20">
            <span className="text-xs font-medium text-muted-foreground">Events</span>
          </div>
          <div className="flex-1 relative" style={{ height: `${ROW_HEIGHT}px` }}>
            {events.map(event => {
              const startMins = timeToMinutes(event.start_time);
              const endMins = timeToMinutes(event.end_time);
              const leftPercent = ((startMins - openMinutes) / totalMinutes) * 100;
              const widthPercent = ((endMins - startMins) / totalMinutes) * 100;
              
              return (
                <Popover key={event.id}>
                  <PopoverTrigger asChild>
                    <div
                      className="absolute top-1 h-[calc(100%-8px)] rounded-md px-2 py-1 text-xs font-medium text-white truncate cursor-pointer hover:opacity-80 transition-opacity"
                      style={{
                        left: `${leftPercent}%`,
                        width: `${widthPercent}%`,
                        backgroundColor: getEventColor(event.color),
                        minWidth: '40px'
                      }}
                      title={`${event.name}: ${event.start_time} - ${event.end_time}`}
                    >
                      {event.name}
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="relative">
                      <div 
                        className="h-2 rounded-t-md"
                        style={{ backgroundColor: getEventColor(event.color) }}
                      />
                      <div className="p-4 space-y-3">
                        <div>
                          <h4 className="font-semibold text-lg">{event.name}</h4>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                            <Clock className="w-4 h-4" />
                            <span className="font-medium">
                              {event.start_time.slice(0, 5)} - {event.end_time.slice(0, 5)}
                            </span>
                          </div>
                        </div>
                        {event.notes ? (
                          <div className="pt-3 border-t">
                            <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              Manager Notes
                            </p>
                            <p className="text-sm whitespace-pre-wrap bg-muted/50 rounded-md p-3">
                              {event.notes}
                            </p>
                          </div>
                        ) : (
                          <div className="pt-3 border-t">
                            <p className="text-xs text-muted-foreground italic">No additional notes</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              );
            })}
          </div>
        </div>
      )}

      {/* Employee Rows */}
      <ScrollArea className="max-h-[600px]">
        {employees.map((employee) => {
          const employeeShifts = shifts.filter(s => s.employee_id === employee.id);
          const isCurrentUser = employee.id === currentEmployeeId;
          
          return (
            <div
              key={employee.id}
              className={cn(
                "flex border-b last:border-b-0",
                isCurrentUser && "bg-violet-50/50 dark:bg-violet-950/20"
              )}
            >
              {/* Employee Info */}
              <div className={cn(
                "w-[100px] sm:w-[150px] flex-shrink-0 px-2 sm:px-3 py-2 border-r flex items-center gap-2",
                isCurrentUser && "bg-violet-100/50 dark:bg-violet-900/30"
              )}>
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: employee.color || '#6366f1' }}
                />
                <div className="min-w-0">
                  <p className={cn(
                    "text-xs sm:text-sm font-medium truncate",
                    isCurrentUser && "text-violet-700 dark:text-violet-300"
                  )}>
                    {employee.name}
                    {isCurrentUser && <span className="ml-1 text-[10px]">(You)</span>}
                  </p>
                </div>
              </div>
              
              {/* Timeline */}
              <div className="flex-1 relative" style={{ height: `${ROW_HEIGHT}px` }}>
                {/* Grid lines */}
                {hourMarks.map((mark, idx) => (
                  <div
                    key={idx}
                    className="absolute top-0 bottom-0 border-l border-dashed border-muted/30 hidden sm:block"
                    style={{ left: `${mark.percent}%` }}
                  />
                ))}
                
                {/* Shifts */}
                {employeeShifts.map(shift => {
                  const startMins = timeToMinutes(shift.start_time);
                  const endMins = timeToMinutes(shift.end_time);
                  const leftPercent = ((startMins - openMinutes) / totalMinutes) * 100;
                  const widthPercent = ((endMins - startMins) / totalMinutes) * 100;
                  
                  return (
                    <div
                      key={shift.id}
                      className="absolute top-1 h-[calc(100%-8px)] rounded-md px-2 flex items-center text-xs text-white font-medium"
                      style={{
                        left: `${leftPercent}%`,
                        width: `${widthPercent}%`,
                        backgroundColor: employee.color || '#6366f1',
                        minWidth: '50px'
                      }}
                      title={`${shift.start_time} - ${shift.end_time}`}
                    >
                      <span className="truncate">
                        {shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)}
                      </span>
                      {isCurrentUser && (
                        <span className="ml-1 bg-white/30 text-[9px] px-1 rounded">You</span>
                      )}
                    </div>
                  );
                })}
                
                {/* Empty state */}
                {employeeShifts.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground/50">
                    No shift
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </ScrollArea>
    </Card>
  );
};

// Helper function to convert color to rgba with opacity
// Layout 2: Cards - Compact single-row cards (Mobile-optimized)
const DayCardsLayout = ({ employees, shifts, events, currentEmployeeId, hasEventAccess, showNotWorking }) => {
  // Sort: current user first, then others
  const sortedEmployees = [...employees].sort((a, b) => {
    if (a.id === currentEmployeeId) return -1;
    if (b.id === currentEmployeeId) return 1;
    return a.name.localeCompare(b.name);
  });

  const employeesWithShifts = sortedEmployees.filter(emp => 
    shifts.some(s => s.employee_id === emp.id)
  );
  
  const employeesWithoutShifts = sortedEmployees.filter(emp => 
    !shifts.some(s => s.employee_id === emp.id)
  );

  return (
    <div className="space-y-3">
      {/* Events Section - Visible cards like My Shifts view */}
      {hasEventAccess && events.length > 0 && (
        <Card className="overflow-hidden">
          <div className="py-2 px-3 bg-slate-50 dark:bg-slate-900/30 border-b">
            <p className="text-xs font-medium text-muted-foreground">Events</p>
          </div>
          <div className="p-2 space-y-1.5">
            {events.map(event => (
              <EventItem key={event.id} event={event} variant="card" />
            ))}
          </div>
        </Card>
      )}

      {/* Working Today - Styled like event cards */}
      {employeesWithShifts.length > 0 && (
        <Card className="overflow-hidden">
          <div className="py-2 px-3 bg-slate-50 dark:bg-slate-900/30 border-b">
            <p className="text-xs font-medium text-muted-foreground">Working Today</p>
          </div>
          <div className="p-2 space-y-1.5">
            {employeesWithShifts.map(employee => {
              const employeeShifts = shifts.filter(s => s.employee_id === employee.id);
              const isCurrentUser = employee.id === currentEmployeeId;
              const empColor = employee.color || '#6366f1';

              return employeeShifts.map(shift => (
                <div
                  key={shift.id}
                  className="relative flex items-center gap-3 p-2.5 rounded-lg"
                  style={{ backgroundColor: colorWithOpacity(empColor, '12') }}
                  data-testid={isCurrentUser ? "my-shift-card" : `employee-card-${employee.id}`}
                >
                  {/* "You" indicator icon in top right */}
                  {isCurrentUser && (
                    <div className="absolute -top-1 -right-1 bg-violet-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                      You
                    </div>
                  )}
                  
                  {/* Color bar - solid */}
                  <div
                    className="w-1.5 h-full min-h-[32px] rounded-full flex-shrink-0"
                    style={{ backgroundColor: empColor }}
                  />
                  
                  {/* Avatar - solid */}
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                    style={{ backgroundColor: empColor }}
                  >
                    {employee.name.charAt(0).toUpperCase()}
                  </div>
                  
                  {/* Name & Time - stacked like events */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {employee.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)}
                    </p>
                  </div>
                </div>
              ));
            })}
          </div>
        </Card>
      )}

      {/* Not Working - Styled like events/working today - Only show if preference enabled */}
      {showNotWorking && employeesWithoutShifts.length > 0 && (
        <Card className="overflow-hidden">
          <div className="py-2 px-3 bg-slate-50 dark:bg-slate-900/30 border-b">
            <p className="text-xs font-medium text-muted-foreground">Not Working</p>
          </div>
          <div className="p-2 space-y-1.5">
            {employeesWithoutShifts.map(employee => {
              const isCurrentUser = employee.id === currentEmployeeId;
              const empColor = employee.color || '#6366f1';
              return (
                <div
                  key={employee.id}
                  className="relative flex items-center gap-3 p-2.5 rounded-lg"
                  style={{ backgroundColor: colorWithOpacity(empColor, '12') }}
                >
                  {/* "You" indicator icon in top right */}
                  {isCurrentUser && (
                    <div className="absolute -top-1 -right-1 bg-violet-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                      You
                    </div>
                  )}
                  
                  {/* Color bar - solid */}
                  <div
                    className="w-1.5 h-full min-h-[32px] rounded-full flex-shrink-0"
                    style={{ backgroundColor: empColor }}
                  />
                  
                  {/* Avatar - solid */}
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                    style={{ backgroundColor: empColor }}
                  >
                    {employee.name.charAt(0).toUpperCase()}
                  </div>
                  
                  {/* Name & Off status */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">
                      {employee.name}
                    </p>
                    <p className="text-xs text-muted-foreground">Off today</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Empty state */}
      {employeesWithShifts.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">No shifts scheduled for today</p>
        </Card>
      )}
    </div>
  );
};

// Layout 3: My Shifts - Focus on current user (Mobile-optimized)
const DayMyShiftsLayout = ({ employees, shifts, events, currentEmployeeId, hasEventAccess, employeeMap }) => {
  const myShifts = shifts.filter(s => s.employee_id === currentEmployeeId);
  const coworkerShifts = shifts.filter(s => s.employee_id !== currentEmployeeId);
  const currentEmployee = employeeMap[currentEmployeeId];
  const empColor = currentEmployee?.color || '#6366f1';

  // Group coworkers by overlapping shifts
  const getOverlappingCoworkers = (myShift) => {
    const myStart = timeToMinutes(myShift.start_time);
    const myEnd = timeToMinutes(myShift.end_time);
    
    return coworkerShifts.filter(shift => {
      const start = timeToMinutes(shift.start_time);
      const end = timeToMinutes(shift.end_time);
      return start < myEnd && end > myStart;
    }).map(shift => ({
      ...shift,
      employee: employeeMap[shift.employee_id]
    }));
  };

  return (
    <div className="space-y-3">
      {/* My Shifts Card */}
      <Card className="overflow-hidden ring-2 ring-violet-400/50">
        {/* Header with clock icon on right */}
        <div className="py-2 px-3 bg-gradient-to-r from-violet-50 to-teal-50 dark:from-violet-950/30 dark:to-teal-950/30 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
              style={{ backgroundColor: empColor }}
            >
              {currentEmployee?.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold text-violet-700 dark:text-violet-300">
                Your Shifts
              </p>
              <p className="text-xs text-muted-foreground">{currentEmployee?.role || 'Staff'}</p>
            </div>
          </div>
          <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
            <Clock className="w-5 h-5 text-violet-600 dark:text-violet-400" />
          </div>
        </div>
        
        {myShifts.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-muted-foreground">You&apos;re not scheduled to work today</p>
            <p className="text-xs text-muted-foreground mt-1">Enjoy your day off! 🎉</p>
          </div>
        ) : (
          <div className="p-3 space-y-3">
            {myShifts.map(shift => {
              const hours = calculateBillableHours(shift, []);
              const overlapping = getOverlappingCoworkers(shift);
              
              return (
                <div key={shift.id}>
                  {/* Time Display - Simplified */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div>
                      <p className="text-2xl font-bold">{shift.start_time.slice(0, 5)}</p>
                      <p className="text-xs text-muted-foreground">Start</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-lg">{hours.toFixed(1)}h</p>
                      <p className="text-xs text-muted-foreground">Duration</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{shift.end_time.slice(0, 5)}</p>
                      <p className="text-xs text-muted-foreground">End</p>
                    </div>
                  </div>
                  
                  {/* Working With - Using standard employee cards */}
                  {overlapping.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-muted-foreground mb-2 px-1">Working with:</p>
                      <div className="space-y-1.5">
                        {overlapping.map(item => {
                          const coworkerColor = item.employee?.color || '#6366f1';
                          return (
                            <div
                              key={item.id}
                              className="flex items-center gap-3 p-2.5 rounded-lg"
                              style={{ backgroundColor: colorWithOpacity(coworkerColor, '12') }}
                            >
                              <div
                                className="w-1.5 h-full min-h-[32px] rounded-full flex-shrink-0"
                                style={{ backgroundColor: coworkerColor }}
                              />
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                                style={{ backgroundColor: coworkerColor }}
                              >
                                {item.employee?.name?.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{item.employee?.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {item.start_time.slice(0, 5)} - {item.end_time.slice(0, 5)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Events (if any) */}
      {hasEventAccess && events.length > 0 && (
        <Card className="overflow-hidden">
          <div className="py-2 px-3 bg-slate-50 dark:bg-slate-900/30 border-b">
            <p className="text-xs font-medium text-muted-foreground">Today&apos;s Events</p>
          </div>
          <div className="p-2 space-y-1.5">
            {events.map(event => (
              <EventItem key={event.id} event={event} variant="card" />
            ))}
          </div>
        </Card>
      )}

      {/* Team Schedule - Container card with employee cards inside */}
      {coworkerShifts.length > 0 && (
        <Card className="overflow-hidden">
          <div className="py-2 px-3 bg-slate-50 dark:bg-slate-900/30 border-b">
            <p className="text-xs font-medium text-muted-foreground">Team Schedule</p>
          </div>
          <div className="p-2 space-y-1.5">
            {[...coworkerShifts].sort((a, b) => a.start_time.localeCompare(b.start_time)).map(shift => {
              const employee = employeeMap[shift.employee_id];
              const teamColor = employee?.color || '#6366f1';
              return (
                <div
                  key={shift.id}
                  className="flex items-center gap-3 p-2.5 rounded-lg"
                  style={{ backgroundColor: colorWithOpacity(teamColor, '12') }}
                >
                  <div
                    className="w-1.5 h-full min-h-[32px] rounded-full flex-shrink-0"
                    style={{ backgroundColor: teamColor }}
                  />
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                    style={{ backgroundColor: teamColor }}
                  >
                    {employee?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{employee?.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

// Staff Week View Component
const StaffWeekView = ({ weekStart, employees, shifts, events, businessHours, hoursOverrides, currentEmployeeId, hasEventAccess, layout, showNotWorking }) => {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  
  // Get employee lookup
  const employeeMap = employees.reduce((acc, emp) => {
    acc[emp.id] = emp;
    return acc;
  }, {});

  // Render based on layout (cards or myshifts use mobile layout)
  if (layout === "cards" || layout === "myshifts") {
    return (
      <WeekMobileLayout
        days={days}
        employees={employees}
        shifts={shifts}
        events={events}
        businessHours={businessHours}
        hoursOverrides={hoursOverrides}
        currentEmployeeId={currentEmployeeId}
        hasEventAccess={hasEventAccess}
        employeeMap={employeeMap}
        layout={layout}
        showNotWorking={showNotWorking}
      />
    );
  }

  // Default: Timeline layout
  return (
    <WeekTimelineLayout
      days={days}
      employees={employees}
      shifts={shifts}
      events={events}
      businessHours={businessHours}
      hoursOverrides={hoursOverrides}
      currentEmployeeId={currentEmployeeId}
      hasEventAccess={hasEventAccess}
    />
  );
};

// Week Timeline Layout (Original)
const WeekTimelineLayout = ({ days, employees, shifts, events, businessHours, hoursOverrides, currentEmployeeId, hasEventAccess }) => {
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  return (
    <Card className="overflow-hidden">
      {/* Days Header */}
      <div className="flex border-b bg-muted/30">
        <div className="w-[150px] flex-shrink-0 px-3 py-2 border-r">
          <span className="text-xs font-medium text-muted-foreground">Employee</span>
        </div>
        {days.map((day, idx) => {
          const dayStr = format(day, 'yyyy-MM-dd');
          const isToday = dayStr === todayStr;
          const dayHours = getEffectiveHoursForDate(dayStr, businessHours, hoursOverrides);
          
          return (
            <div
              key={idx}
              className={cn(
                "flex-1 px-2 py-2 text-center border-r last:border-r-0",
                isToday && "bg-violet-100/50 dark:bg-violet-900/30"
              )}
            >
              <p className={cn(
                "text-xs font-medium",
                isToday ? "text-violet-700 dark:text-violet-300" : "text-muted-foreground"
              )}>
                {format(day, 'EEE')}
              </p>
              <p className={cn(
                "text-sm font-bold",
                isToday ? "text-violet-700 dark:text-violet-300" : ""
              )}>
                {format(day, 'd')}
              </p>
              {!dayHours?.is_open && (
                <Badge variant="outline" className="text-[10px] px-1 py-0 mt-1">Closed</Badge>
              )}
            </div>
          );
        })}
      </div>

      {/* Events Row */}
      {hasEventAccess && (
        <div className="flex border-b bg-slate-50/50 dark:bg-slate-900/30">
          <div className="w-[150px] flex-shrink-0 px-3 py-2 border-r bg-muted/20">
            <span className="text-xs font-medium text-muted-foreground">Events</span>
          </div>
          {days.map((day, idx) => {
            const dayStr = format(day, 'yyyy-MM-dd');
            const dayEvents = events.filter(e => e.date === dayStr);
            
            return (
              <div
                key={idx}
                className="flex-1 px-1 py-1 border-r last:border-r-0 min-h-[40px]"
              >
                {dayEvents.map(event => (
                  <EventItem key={event.id} event={event} variant="mini" />
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Employee Rows */}
      <ScrollArea className="max-h-[500px]">
        {employees.map((employee) => {
          const isCurrentUser = employee.id === currentEmployeeId;
          
          return (
            <div
              key={employee.id}
              className={cn(
                "flex border-b last:border-b-0",
                isCurrentUser && "bg-violet-50/50 dark:bg-violet-950/20"
              )}
            >
              {/* Employee Info */}
              <div className={cn(
                "w-[150px] flex-shrink-0 px-3 py-2 border-r flex items-center gap-2",
                isCurrentUser && "bg-violet-100/50 dark:bg-violet-900/30"
              )}>
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: employee.color || '#6366f1' }}
                />
                <div className="min-w-0">
                  <p className={cn(
                    "text-sm font-medium truncate",
                    isCurrentUser && "text-violet-700 dark:text-violet-300"
                  )}>
                    {employee.name}
                    {isCurrentUser && <span className="ml-1 text-xs">(You)</span>}
                  </p>
                </div>
              </div>
              
              {/* Days */}
              {days.map((day, idx) => {
                const dayStr = format(day, 'yyyy-MM-dd');
                const isToday = dayStr === todayStr;
                const dayShifts = shifts.filter(s => s.employee_id === employee.id && s.date === dayStr);
                const dayHours = getEffectiveHoursForDate(dayStr, businessHours, hoursOverrides);
                
                return (
                  <div
                    key={idx}
                    className={cn(
                      "flex-1 px-1 py-1 border-r last:border-r-0 min-h-[50px]",
                      isToday && "bg-violet-50/30 dark:bg-violet-950/10",
                      !dayHours?.is_open && "bg-muted/20"
                    )}
                  >
                    {dayShifts.map(shift => (
                      <div
                        key={shift.id}
                        className="text-[11px] text-white rounded px-1 py-0.5 mb-0.5 flex items-center gap-0.5"
                        style={{ backgroundColor: employee.color || '#6366f1' }}
                      >
                        <span>{shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)}</span>
                        {isCurrentUser && <span className="bg-white/30 text-[8px] px-0.5 rounded">You</span>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })}
      </ScrollArea>
    </Card>
  );
};

// Week Mobile Layout (Cards/MyShifts for week view)
const WeekMobileLayout = ({ days, employees, shifts, events, businessHours, hoursOverrides, currentEmployeeId, hasEventAccess, employeeMap, layout, showNotWorking }) => {
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const currentEmployee = employeeMap[currentEmployeeId];

  // For "My Shifts" layout, focus on current user
  if (layout === "myshifts") {
    const myWeekShifts = shifts.filter(s => s.employee_id === currentEmployeeId);
    const myWeekHours = myWeekShifts.reduce((acc, shift) => {
      return acc + calculateBillableHours(shift, []);
    }, 0);
    const empColor = currentEmployee?.color || '#6366f1';

    return (
      <div className="space-y-3">
        {/* Summary Card */}
        <Card className="overflow-hidden">
          <div className="py-2 px-3 bg-violet-50 dark:bg-violet-950/30 border-b">
            <p className="text-xs font-medium text-muted-foreground">Your Week</p>
          </div>
          <div className="p-2">
            <div
              className="flex items-center gap-3 p-2.5 rounded-lg"
              style={{ backgroundColor: colorWithOpacity(empColor, '12') }}
            >
              <div
                className="w-1.5 h-full min-h-[32px] rounded-full flex-shrink-0"
                style={{ backgroundColor: empColor }}
              />
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                style={{ backgroundColor: empColor }}
              >
                {currentEmployee?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-violet-700 dark:text-violet-300">
                  {currentEmployee?.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {myWeekShifts.length} shifts · {myWeekHours.toFixed(1)} hours
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Day by Day */}
        {days.map(day => {
          const dayStr = format(day, 'yyyy-MM-dd');
          const isToday = dayStr === todayStr;
          const dayHours = getEffectiveHoursForDate(dayStr, businessHours, hoursOverrides);
          const myShifts = shifts.filter(s => s.employee_id === currentEmployeeId && s.date === dayStr);
          const dayEvents = hasEventAccess ? events.filter(e => e.date === dayStr) : [];

          return (
            <Card 
              key={dayStr} 
              className={cn(
                "overflow-hidden",
                isToday && "ring-2 ring-violet-400/50"
              )}
            >
              {/* Day Header */}
              <div className={cn(
                "py-2 px-3 border-b flex items-center justify-between",
                isToday ? "bg-violet-50 dark:bg-violet-950/30" : "bg-slate-50 dark:bg-slate-900/30"
              )}>
                <div className="flex items-center gap-2">
                  <p className={cn(
                    "font-semibold text-sm",
                    isToday && "text-violet-700 dark:text-violet-300"
                  )}>
                    {format(day, 'EEEE')}
                  </p>
                  {isToday && <Badge className="bg-violet-600 text-xs">Today</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">{format(day, 'd MMM')}</p>
              </div>

              {!dayHours?.is_open ? (
                <div className="p-3 text-center text-sm text-muted-foreground">
                  Business closed
                </div>
              ) : (
                <div className="p-2 space-y-3">
                  {/* Events */}
                  {dayEvents.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground px-1">Events</p>
                      {dayEvents.map(event => (
                        <EventItem key={event.id} event={event} variant="card" />
                      ))}
                    </div>
                  )}

                  {/* My Shifts */}
                  {myShifts.length > 0 ? (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground px-1">Your Shift</p>
                      {myShifts.map(shift => (
                        <div
                          key={shift.id}
                          className="relative flex items-center gap-3 p-2.5 rounded-lg"
                          style={{ backgroundColor: colorWithOpacity(empColor, '12') }}
                        >
                          {/* "You" indicator icon in top right */}
                          <div className="absolute -top-1 -right-1 bg-violet-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                            You
                          </div>
                          
                          <div
                            className="w-1.5 h-full min-h-[32px] rounded-full flex-shrink-0"
                            style={{ backgroundColor: empColor }}
                          />
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                            style={{ backgroundColor: empColor }}
                          >
                            {currentEmployee?.name?.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">
                              {currentEmployee?.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground px-1">Your Shift</p>
                      <div
                        className="relative flex items-center gap-3 p-2.5 rounded-lg"
                        style={{ backgroundColor: colorWithOpacity(empColor, '12') }}
                      >
                        {/* "You" indicator icon in top right */}
                        <div className="absolute -top-1 -right-1 bg-violet-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                          You
                        </div>
                        
                        <div
                          className="w-1.5 h-full min-h-[32px] rounded-full flex-shrink-0"
                          style={{ backgroundColor: empColor }}
                        />
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                          style={{ backgroundColor: empColor }}
                        >
                          {currentEmployee?.name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">
                            {currentEmployee?.name}
                          </p>
                          <p className="text-xs text-muted-foreground">Day off</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    );
  }

  // Cards layout for week (All Staff view)
  return (
    <div className="space-y-3">
      {days.map(day => {
        const dayStr = format(day, 'yyyy-MM-dd');
        const isToday = dayStr === todayStr;
        const dayHours = getEffectiveHoursForDate(dayStr, businessHours, hoursOverrides);
        const dayShifts = shifts.filter(s => s.date === dayStr);
        const dayEvents = hasEventAccess ? events.filter(e => e.date === dayStr) : [];
        
        // Separate employees with and without shifts
        const employeesWithShifts = employees.filter(emp => 
          dayShifts.some(s => s.employee_id === emp.id)
        );
        const employeesWithoutShifts = employees.filter(emp => 
          !dayShifts.some(s => s.employee_id === emp.id)
        );

        return (
          <Card 
            key={dayStr}
            className={cn(
              "overflow-hidden",
              isToday && "ring-2 ring-violet-400/50"
            )}
          >
            {/* Day Header */}
            <div className={cn(
              "py-2 px-3 border-b flex items-center justify-between",
              isToday ? "bg-violet-50 dark:bg-violet-950/30" : "bg-slate-50 dark:bg-slate-900/30"
            )}>
              <div className="flex items-center gap-2">
                <p className={cn(
                  "font-semibold text-sm",
                  isToday && "text-violet-700 dark:text-violet-300"
                )}>
                  {format(day, 'EEEE')}
                </p>
                {isToday && <Badge className="bg-violet-600 text-xs">Today</Badge>}
              </div>
              <p className="text-xs text-muted-foreground">{format(day, 'd MMM')}</p>
            </div>

            {/* Content */}
            {!dayHours?.is_open ? (
              <div className="p-3 text-center text-sm text-muted-foreground">
                Business closed
              </div>
            ) : (
              <div className="p-2 space-y-3">
                {/* Events */}
                {dayEvents.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground px-1">Events</p>
                    {dayEvents.map(event => (
                      <EventItem key={event.id} event={event} variant="card" />
                    ))}
                  </div>
                )}

                {/* Working */}
                {employeesWithShifts.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground px-1">Working</p>
                    {employeesWithShifts.map(employee => {
                      const employeeShifts = dayShifts.filter(s => s.employee_id === employee.id);
                      const isCurrentUser = employee.id === currentEmployeeId;
                      const empColor = employee.color || '#6366f1';

                      return employeeShifts.map(shift => (
                        <div
                          key={shift.id}
                          className="relative flex items-center gap-3 p-2.5 rounded-lg"
                          style={{ backgroundColor: colorWithOpacity(empColor, '12') }}
                        >
                          {/* "You" indicator icon in top right */}
                          {isCurrentUser && (
                            <div className="absolute -top-1 -right-1 bg-violet-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                              You
                            </div>
                          )}
                          
                          {/* Color bar */}
                          <div
                            className="w-1.5 h-full min-h-[32px] rounded-full flex-shrink-0"
                            style={{ backgroundColor: empColor }}
                          />
                          {/* Avatar */}
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                            style={{ backgroundColor: empColor }}
                          >
                            {employee.name.charAt(0).toUpperCase()}
                          </div>
                          {/* Name & Time */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {employee.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)}
                            </p>
                          </div>
                        </div>
                      ));
                    })}
                  </div>
                )}

                {/* Not Working - Only show if preference enabled */}
                {showNotWorking && employeesWithoutShifts.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground px-1">Not Working</p>
                    {employeesWithoutShifts.map(employee => {
                      const isCurrentUser = employee.id === currentEmployeeId;
                      const empColor = employee.color || '#6366f1';
                      return (
                        <div
                          key={employee.id}
                          className="relative flex items-center gap-3 p-2.5 rounded-lg"
                          style={{ backgroundColor: colorWithOpacity(empColor, '12') }}
                        >
                          {/* "You" indicator icon in top right */}
                          {isCurrentUser && (
                            <div className="absolute -top-1 -right-1 bg-violet-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-sm">
                              You
                            </div>
                          )}
                          
                          {/* Color bar */}
                          <div
                            className="w-1.5 h-full min-h-[32px] rounded-full flex-shrink-0"
                            style={{ backgroundColor: empColor }}
                          />
                          {/* Avatar */}
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                            style={{ backgroundColor: empColor }}
                          >
                            {employee.name.charAt(0).toUpperCase()}
                          </div>
                          {/* Name & Off */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {employee.name}
                            </p>
                            <p className="text-xs text-muted-foreground">Off</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Empty state */}
                {employeesWithShifts.length === 0 && (!showNotWorking || employeesWithoutShifts.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-2">No shifts scheduled</p>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

// Helper functions
function timeToMinutes(time) {
  if (!time) return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

function minutesToTime(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

export default StaffDashboard;
